// A cell picked on a board with no trace's reading behind it (a workspace of
// the reader's own, or one whose anchor is still being read), for its
// workspace's dock to read as a trace's dock reads one: { board, key,
// session, title, laneId, turn, base, record: { model, turns, endpointId },
// keep }. Each pick is the reader's (a click), so each asks the dock to show
// it (`ask`); a board lets go of its own when it goes.
//
// And the trace last selected on the board in front, for the dock's Reading
// to read as a trace's own dock reads its trace: { board, key, session,
// title, anchor, trace, turns }. `anchor`: the board's own trace, which the
// board reads in the dock already (dockBridge.js). It stays until another
// trace is selected, its card goes, or its board does.
//
// Each dock has its own (`scope`, as dockBridge.js names them): a cell picked
// on a session previewed over the home's panes reads in the preview's dock,
// and is not left in the home's for when the preview goes.
import { useSyncExternalStore } from "react";
import { useDockScope } from "./dockBridge.js";

const picks = new Map();
const cards = new Map();
let asks = 0;
const subs = new Set();
const emit = () => { for (const fn of [...subs]) fn(); };
const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };

export function setBoardPick(next, scope = "home") {
  if (!next) return;
  asks += 1;
  picks.set(scope, { ...next, ask: asks });
  emit();
}

// Lets go of `board`'s pick, if the one shown is still its own (any board's,
// with none named).
export function clearBoardPick(board = null, scope = "home") {
  const picked = picks.get(scope);
  if (!picked || (board != null && picked.board !== board)) return;
  picks.delete(scope);
  emit();
}

export function boardPick(scope = "home") {
  return picks.get(scope) || null;
}

// Where the dock reading pick `ask` has stepped to since it opened (`at`:
// { laneId, turn }), for its tab to name; the pick itself, and so the dock's
// own place, stays as clicked.
export function followBoardPick(ask, at, scope = "home") {
  const picked = picks.get(scope);
  if (!picked || picked.ask !== ask || !at) return;
  if (picked.at?.laneId === at.laneId && picked.at?.turn === at.turn) return;
  picks.set(scope, { ...picked, at: { laneId: at.laneId ?? null, turn: at.turn } });
  emit();
}

// The pick of the dock a component is drawn in, or of the one named.
export function useBoardPick(scope = null) {
  const given = useDockScope();
  const at = scope || given;
  return useSyncExternalStore(subscribe, () => picks.get(at) || null, () => picks.get(at) || null);
}

// A board says what it has selected as it renders: the same trace again,
// its record no different, changes nothing.
const sameCard = (a, b) => a.board === b.board && a.key === b.key && a.session === b.session && a.title === b.title
  && !!a.anchor === !!b.anchor && a.turns === b.turns && (a.trace?.id ?? null) === (b.trace?.id ?? null);

export function setBoardCard(next, scope = "home") {
  const chosen = cards.get(scope);
  if (!next || (chosen && sameCard(chosen, next))) return;
  cards.set(scope, next);
  emit();
}

// Lets go of `board`'s trace, if the one shown is still its own (any board's,
// with none named): of any of its own, or of the one keyed `key`.
export function clearBoardCard(board = null, key = null, scope = "home") {
  const chosen = cards.get(scope);
  if (!chosen || (board != null && chosen.board !== board) || (key != null && chosen.key !== key)) return;
  cards.delete(scope);
  emit();
}

export function boardCard(scope = "home") {
  return cards.get(scope) || null;
}

export function useBoardCard(scope = null) {
  const given = useDockScope();
  const at = scope || given;
  return useSyncExternalStore(subscribe, () => cards.get(at) || null, () => cards.get(at) || null);
}
